import { AmbientScribeEmbed } from "@accurx/ambient-scribe";
import {
    FeatureName,
    WorkspaceFeatureGuard,
    useCurrentUser,
    useCurrentWorkspace,
    useFeatureFlag,
} from "@accurx/auth";
import { useIsGpBatchEnabled } from "@accurx/batch-messaging";
import { WORKSPACE_MANAGEMENT_PRIMARY_ROUTES } from "@accurx/workspace-management";
import NotFound from "NotFound";
import { Switch } from "react-router";

import { AppRoute, SimpleRoute } from "app/AppRoute";
import { FeatureGuard } from "app/FeatureGuard";
import { BatchMessageTrustSection } from "app/batchMessage/trust/BatchMessageTrustSection";
import { GuardedRoute } from "app/guardedRoutes/GuardedRoute";
import integratedClinicRoutes from "app/patientLists/integratedClinics/routes";
import { RequestMedicalRecord } from "app/recordView/RequestMedicalRecord";
import { RedirectToUnifiedInbox } from "app/workspace/RedirectToUnifiedInbox";
import { MapWorkspacePage } from "app/workspace/pages/MapWorkspace/MapWorkspacePage";
import { PatientHelper } from "shared/PatientHelper";
import { ROUTES, ROUTES_PRIMARY, ROUTES_WORKSPACE } from "shared/Routes";
import { ConversationManagerProvider } from "shared/concierge/conversations/context/ConversationManagerContext";
import { PatientManagerProvider } from "shared/concierge/patients/context/PatientManagerContext";
import { UsersAndTeamsManagerProvider } from "shared/concierge/usersAndTeams/context/UsersAndTeamsContext";

import { WorkspaceOnboardingRoutesSection } from "../guardedRoutes/routes/WorkspaceOnboardingRoutesSection";
import { WorkspaceSettingsRoutesSection } from "../guardedRoutes/routes/WorkspaceSettingsRoutesSection";
import { WorkspaceConversationsSection } from "../workspaceConversations/WorkspaceConversationsSection";
import { SearchedPatientSection } from "./SearchedPatientSection";
import { VoipPage } from "./pages/VoipPage/VoipPage";

export const WorkspaceRoutes = (): JSX.Element => {
    const { user } = useCurrentUser();
    const { orgId: workspaceId } = useCurrentWorkspace();

    const isTrustBatchEnabled = useFeatureFlag(FeatureName.WebBatchMessaging);
    const isGpBatchEnabled = useIsGpBatchEnabled();

    const canSendBatchMessageTrust = isTrustBatchEnabled || isGpBatchEnabled;

    const hasIntegratedClinicsEnabled = useFeatureFlag(
        FeatureName.IntegratedClinicLists,
    );

    return (
        // Include the layout within the conversation manager provider so the full layout can access the conversation manager
        <PatientManagerProvider workspaceId={workspaceId}>
            <UsersAndTeamsManagerProvider workspaceId={workspaceId}>
                <ConversationManagerProvider
                    workspaceId={workspaceId}
                    is2FAed={user.is2FAed}
                >
                    <Switch>
                        <SimpleRoute
                            path={
                                WORKSPACE_MANAGEMENT_PRIMARY_ROUTES.workspaceOnboarding
                            }
                        >
                            <WorkspaceOnboardingRoutesSection />
                        </SimpleRoute>
                        <SimpleRoute path={ROUTES_PRIMARY.workspaceSettings}>
                            <WorkspaceSettingsRoutesSection />
                        </SimpleRoute>
                        <GuardedRoute
                            requires={{
                                authenticationStatus: "LoggedInWith2FA",
                                onboarded: true,
                            }}
                            path={ROUTES_PRIMARY.workspaceConversations}
                        >
                            <WorkspaceConversationsSection
                                workspaceId={workspaceId}
                            />
                        </GuardedRoute>
                        <AppRoute
                            path={ROUTES_PRIMARY.workspacePatientProfile}
                            allowRoute={{
                                isAllowed:
                                    PatientHelper.getPatientTokenWithStorageFallback(
                                        null,
                                    ) !== "",
                                redirectTo: ROUTES.home,
                            }}
                        >
                            <SearchedPatientSection />
                        </AppRoute>
                        <AppRoute
                            allowRoute={{
                                isAllowed: canSendBatchMessageTrust,
                                redirectTo: ROUTES.home,
                            }}
                            path={ROUTES_PRIMARY.batchMessageTrust}
                        >
                            <BatchMessageTrustSection />
                        </AppRoute>
                        {hasIntegratedClinicsEnabled &&
                            integratedClinicRoutes.map(
                                ({
                                    Component,
                                    isTwoFactorRequired,
                                    ...routeProps
                                }) =>
                                    isTwoFactorRequired ? (
                                        <GuardedRoute
                                            key={routeProps.path}
                                            requires={{
                                                authenticationStatus:
                                                    "LoggedInWith2FA",
                                                onboarded: true,
                                            }}
                                            {...routeProps}
                                        >
                                            <Component />
                                        </GuardedRoute>
                                    ) : (
                                        <SimpleRoute
                                            key={routeProps.path}
                                            {...routeProps}
                                        >
                                            <Component />
                                        </SimpleRoute>
                                    ),
                            )}
                        <GuardedRoute
                            requires={{
                                authenticationStatus: "LoggedInWith2FA",
                                onboarded: true,
                            }}
                            path={ROUTES_WORKSPACE.remoteRecordView}
                            exact
                        >
                            <FeatureGuard all={[FeatureName.RecordAccessBeta]}>
                                <RequestMedicalRecord />
                            </FeatureGuard>
                        </GuardedRoute>
                        <SimpleRoute
                            path={"/w/:workspaceId/clinician-conversations"}
                        >
                            <RedirectToUnifiedInbox workspaceId={workspaceId} />
                        </SimpleRoute>
                        <SimpleRoute path={ROUTES_WORKSPACE.voip}>
                            <VoipPage />
                        </SimpleRoute>
                        <GuardedRoute
                            requires={{
                                authenticationStatus: "LoggedInWith2FA",
                                onboarded: false,
                                // desktop users have their own T&C in desktop anyway, so no need to protect this page by 'web' T&C
                                // `onboarded` also means that user is in at least 1 workspace,
                                // but we are safe to have false here as user gets here only actually being in a workspace.
                                // this page has the safe guard set as desktop-launch page
                            }}
                            path={
                                ROUTES_WORKSPACE.workspaceMapWorkspaceToOrganisation
                            }
                            exact
                        >
                            <MapWorkspacePage />
                        </GuardedRoute>
                        <GuardedRoute
                            requires={{
                                onboarded: true,
                                authenticationStatus: "LoggedIn",
                            }}
                            path="/w/:workspaceId/ambient-scribe"
                        >
                            <WorkspaceFeatureGuard
                                all={[FeatureName.AmbientScribeWeb]}
                            >
                                <AmbientScribeEmbed />
                            </WorkspaceFeatureGuard>
                        </GuardedRoute>
                        <SimpleRoute path="*" component={NotFound} />
                    </Switch>
                </ConversationManagerProvider>
            </UsersAndTeamsManagerProvider>
        </PatientManagerProvider>
    );
};
